import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Suspense, lazy } from 'react'
import { AuthContext, useAuthProvider } from '@/hooks/useAuth'
import TabBar from '@/components/layout/TabBar'

// Lazy-loaded pages for better initial load performance
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const PeaksPage = lazy(() => import('@/pages/PeaksPage'))
const InsightsPage = lazy(() => import('@/pages/InsightsPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))

// ─── React Query Client ───────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
      networkMode: 'online',
    },
  },
})

// ─── Global Page Fallback ─────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        backgroundColor: 'var(--bg-primary)',
        padding: '16px',
        paddingTop: 'calc(16px + env(safe-area-inset-top))',
      }}
      aria-busy="true"
      aria-label="Loading page"
    >
      <div className="skeleton" style={{ width: '40%', height: '34px', marginBottom: '20px' }} />
      <div className="skeleton" style={{ width: '100%', height: '120px', borderRadius: '12px', marginBottom: '12px' }} />
      <div className="skeleton" style={{ width: '100%', height: '260px', borderRadius: '12px', marginBottom: '12px' }} />
      <div className="skeleton" style={{ width: '100%', height: '80px', borderRadius: '12px' }} />
    </div>
  )
}

// ─── Auth Guard ───────────────────────────────────────────────────────────────

function AuthGuard() {
  const { isAuthenticated, isLoading } = useAuthProvider()

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          backgroundColor: 'var(--bg-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        aria-busy="true"
        aria-label="Authenticating"
      >
        {/* Simple logo pulse while auth loads */}
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-insulin) 100%)',
            animation: 'pulse-glow 1.5s ease-in-out infinite',
          }}
          aria-hidden="true"
        />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <Outlet />
}

// ─── App Shell (with TabBar) ──────────────────────────────────────────────────

function AppShell() {
  return (
    <>
      <Outlet />
      <TabBar />
    </>
  )
}

// ─── Auth Provider Wrapper ────────────────────────────────────────────────────

function AuthProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuthProvider()
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
}

// ─── Root App ─────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageSkeleton />}>
            <Routes>
              {/* Public route */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected routes */}
              <Route element={<AuthGuard />}>
                <Route element={<AppShell />}>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/peaks" element={<PeaksPage />} />
                  <Route path="/insights" element={<InsightsPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </Route>
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
